import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ScienceIcon from '@mui/icons-material/Science';

const DomainsSection = styled.section`
  background-color: ${props => props.theme.colors.dark};
  position: relative;
  overflow: hidden;
  padding: 6rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  text-align: center;
  margin-bottom: 3rem;
  
  .highlight {
    color: ${props => props.theme.colors.primary};
  }
`;

const DomainsContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto 4rem;
  padding: 0 2rem;
`;

const DomainCard = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 2.5rem;
    top: 5rem;
    bottom: -1rem;
    width: 2px;
    background: ${props => props.theme.colors.primary}15;
    z-index: 1;
  }

  .icon-container {
    position: relative;
    z-index: 2;
    flex-shrink: 0;
    width: 5rem;
    height: 5rem;
    border-radius: 50%;
    background: ${props => props.theme.colors.background};
    border: 2px solid ${props => props.theme.colors.primary}30;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.primary};
    transition: all 0.3s ease;
    
    svg {
      font-size: 2.5rem;
    }
  }

  .content {
    flex-grow: 1;
    padding-top: 0.5rem;
  }

  .domain-title {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    font-weight: 600;
  }

  .domain-description {
    color: ${props => props.theme.colors.gray};
    font-size: 1.1rem;
    line-height: 1.6;
  }

  &:hover {
    .icon-container {
      transform: scale(1.1);
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 20px ${props => props.theme.colors.primary}30;
    }
  }

  @media (max-width: 600px) {
    gap: 1.5rem;
    
    .icon-container {
      width: 4rem;
      height: 4rem;
      
      svg {
        font-size: 2rem;
      }
    }

    &:not(:last-child)::after {
      left: 2rem;
    }

    .domain-title {
      font-size: 1.3rem;
    }

    .domain-description {
      font-size: 1rem;
    }
  }
`;

const domains = [
  {
    title: 'Healthcare',
    icon: <LocalHospitalIcon />,
    description: 'Delivered healthcare solutions from patient management systems to medical device integrations and data analytics — domains where AI-assisted workflows now unlock major efficiency gains.'
  },
  {
    title: 'Banking, Payments',
    icon: <AccountBalanceIcon />,
    description: 'Specialized in secure payment processing systems, banking applications, and financial technology solutions that meet strict regulatory requirements.'
  },
  {
    title: 'E-commerce',
    icon: <ShoppingCartIcon />,
    description: 'Built scalable e-commerce platforms with features like inventory management, payment integration, and personalized shopping experiences.'
  },
  {
    title: 'Life Science',
    icon: <ScienceIcon />,
    description: 'Developed solutions for research institutions and pharmaceutical companies, including data analysis tools and laboratory management systems.'
  }
];

const Domains = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <DomainsSection id="domains">
      <Container>
        <SectionTitle>
          Experience in <span className="highlight">Domains</span>
        </SectionTitle>
        <DomainsContainer ref={ref}>
          {domains.map((domain, index) => (
            <DomainCard
              key={index}
              as={motion.div}
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
            >
              <div className="icon-container">
                {domain.icon}
              </div>
              <div className="content">
                <h4 className="domain-title">{domain.title}</h4>
                <p className="domain-description">{domain.description}</p>
              </div>
            </DomainCard>
          ))}
        </DomainsContainer>
      </Container>
    </DomainsSection>
  );
};

export default Domains;
